import clsx from 'clsx';
import React, { HTMLAttributes } from 'react';

import countryCodesAlpha3 from '~/utils/country-iso-codes/country-iso-alpha-three.json';
import countryCodes from '~/utils/country-iso-codes/country-iso-codes.json';
import regionalCodes from '~/utils/country-iso-codes/regional-codes.json';

import styles from './FlagIcon.module.scss';
import { convertCountryCodeToAlpha2, isRegionalCode } from './utils';

export interface FlagIconProps extends HTMLAttributes<HTMLSpanElement> {
  countryCode?: string;
  variant?: 'default' | 'rounded' | 'circular';
  customSize?: number;
}

export const FlagIcon = ({
  countryCode = '',
  variant = 'default',
  customSize = 14, // Default to 14px (equivalent to previous 'md' size)
  ...rest
}: FlagIconProps) => {
  const countryCodeUpperCase = countryCode.toUpperCase();

  if (!countryCode) {
    return null;
  }

  const isValidCode =
    countryCodesAlpha3.includes(countryCodeUpperCase) ||
    countryCodes.includes(countryCodeUpperCase) ||
    regionalCodes.includes(countryCodeUpperCase);

  if (!isValidCode) {
    return (
      <span
        className={styles.fallback}
        style={{ minHeight: `${customSize}px` }}
        data-testid="fallback"
        {...rest}
      >
        {countryCodeUpperCase}
      </span>
    );
  }

  const alpha2Code = convertCountryCodeToAlpha2(countryCodeUpperCase);

  if (!alpha2Code || isRegionalCode(countryCodeUpperCase)) {
    return (
      <span
        className={styles.fallback}
        style={{ minHeight: `${customSize}px` }}
        data-testid="fallback"
        {...rest}
      >
        {countryCodeUpperCase}
      </span>
    );
  }

  const { className, ...otherProps } = rest;

  const flagClasses = clsx(
    styles.flag,
    'fi',
    `fi-${alpha2Code.toLowerCase()}`,
    variant === 'circular' && 'fis',
    variant !== 'default' && styles[variant],
    className
  );

  const flagStyle = {
    height: `${customSize}px`,
    width: `${variant === 'circular' ? customSize : customSize * 1.5}px`,
  };

  return (
    <span
      className={flagClasses}
      style={flagStyle}
      data-country={countryCodeUpperCase}
      {...otherProps}
    />
  );
};
